import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { LockKey, ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rules = [
  { key: 'len', label: 'At least 8 characters', test: v => v.length >= 8 },
  { key: 'diff', label: 'Different from current password', test: (v, cur) => v && v !== cur },
  { key: 'match', label: 'New password and confirmation match', test: (v, _c, conf) => v && v === conf },
];

export default function ChangePasswordPage() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [conf, setConf] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const mustChange = !!(user && user.must_change_password);

  const allOk = rules.every(r => r.test(next, current, conf));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!allOk) { setErr('Please meet all password requirements.'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: current, new_password: next
      }, { withCredentials: true, headers });
      toast.success('Password updated');
      if (refreshMe) await refreshMe();
      // For mandatory password resets, send the user to the home route so
      // their session lands inside the dashboard. For self-initiated changes
      // from the avatar menu, pop the history stack so they return to the
      // page they were on.
      if (mustChange) {
        navigate('/');
      } else {
        navigate(-1);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to change password');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 px-4" data-testid="change-pwd-page">
      <div className="w-full max-w-md">
        <div className="bg-[#FFFFFF] shadow-xl rounded-2xl p-8 border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#0A2540]/10 flex items-center justify-center">
              <LockKey size={22} weight="fill" className="text-[#0A2540]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {mustChange ? 'Set a new password' : 'Change password'}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {mustChange ? 'Required before you can continue.' : 'Pick something only you know.'}
              </p>
            </div>
          </div>

          {err && (
            <div className="mb-4 rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 text-[#EF4444] text-xs px-3 py-2" data-testid="change-pwd-err">
              {err}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Current password</Label>
              <Input type={show ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)}
                required autoFocus className="rounded-md" data-testid="pwd-current" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">New password</Label>
              <Input type={show ? 'text' : 'password'} value={next} onChange={e => setNext(e.target.value)}
                required className="rounded-md" data-testid="pwd-new" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Confirm new password</Label>
              <Input type={show ? 'text' : 'password'} value={conf} onChange={e => setConf(e.target.value)}
                required className="rounded-md" data-testid="pwd-confirm" />
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-500 select-none cursor-pointer">
              <input type="checkbox" checked={show} onChange={() => setShow(s => !s)} className="accent-slate-900" />
              {show ? <><EyeSlash size={14} /> Hide</> : <><Eye size={14} /> Show</>} passwords
            </label>

            <ul className="space-y-1.5 border-t border-slate-100 pt-3">
              {rules.map(r => {
                const ok = r.test(next, current, conf);
                return (
                  <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? 'text-[#10B981]' : 'text-slate-400'}`}>
                    <ShieldCheck size={12} weight={ok ? 'fill' : 'regular'} /> {r.label}
                  </li>
                );
              })}
            </ul>

            <Button type="submit" disabled={!allOk || submitting}
              className="w-full h-11 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-60"
              data-testid="pwd-submit">
              {submitting ? 'Saving…' : 'Update password'}
            </Button>

            <button type="button" onClick={() => (mustChange ? logout() : navigate(-1))}
              className="w-full text-xs text-slate-400 hover:text-slate-600 mt-2" data-testid="pwd-logout">
              {mustChange ? 'Sign out' : 'Cancel'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
